/* Membership join — storefront.
 *
 * Two entry points, one membership write (the app: custom.membership_status,
 * the mirror tag, consent, scout tags — enrolMember in membership.server.ts):
 *
 *   1. Logged in — the consent-only form POSTs /apps/spejder/membership/join.
 *      Shopify signs `logged_in_customer_id` into the proxy HMAC, so the app
 *      knows who this is without trusting the body. No reload-free patching:
 *      member surfaces are server-rendered, so the page reloads.
 *
 *   2. Logged out — the guest form (name, e-mail, scout, both consents) POSTs
 *      /apps/spejder/membership/signup, which finds-or-creates the customer
 *      record by e-mail, marks it a member, writes the typed names into blank
 *      fields and the ticked consent. Then the SAME cart attributes the
 *      "Opret dig" modal, the notify drawer and the checkout block write go on
 *      the cart (membership_signup=yes & co.), which is what makes the visitor
 *      a member for the rest of the session — member prices, medlemspris
 *      totals, the checkout block confirmed — with no login. The form is
 *      replaced by "Tak, du er nu tilmeldt!" + a "Login" CTA, and the e-mail
 *      is remembered (localStorage) so the login link and — once a Storefront
 *      API token is configured — the checkout are prefilled with it.
 *
 *      Client decision 2026-09-08 (meeting with Spejder Sport): the previous
 *      "TILMELD OG LOGIN" round-trip (stash intent → passwordless login →
 *      replay on return) is gone for the guest forms. The replay code below
 *      is kept only for a stash that is still sitting in a visitor's
 *      sessionStorage from before this change, and for the logged-in path.
 *
 * The accepted risk of enrolling an unverified e-mail is documented on the app
 * route (proxy.membership.signup.tsx): membership carries prices, not access.
 */
(function () {
  var ENDPOINT = '/apps/spejder/membership/join';
  var SIGNUP_ENDPOINT = '/apps/spejder/membership/signup';
  var INTENT_KEY = 'memberJoinIntent';
  /* The e-mail a guest signed up with. localStorage, not sessionStorage: the
     cart (and with it the session membership) outlives the tab, so the
     prefill should too. Only ever an address — never consent, never identity. */
  var EMAIL_KEY = 'spejderMemberEmail';
  /* ?member_joined=cart on the post-signup reload = re-open the cart drawer. */
  var JOINED_PARAM = 'member_joined';

  function fieldValue(root, name) {
    var input = root.querySelector('input[name="' + name + '"]');
    return input ? input.value.trim() : '';
  }

  function readIntent(root) {
    var terms = root.querySelector('[data-member-terms]');
    var marketing = root.querySelector('[data-member-marketing]');
    var scout = root.querySelector('[data-member-scout]');
    var group = root.querySelector('[data-member-scout-group]');
    return {
      termsAccepted: !!(terms && terms.checked),
      // Marketing consent is its own OPTIONAL box (Magnus 2026-08-28) —
      // joining the club never implies it. Unticked posts false, which the
      // app treats as "leave any existing subscription alone", never as an
      // unsubscribe.
      marketingConsent: !!(marketing && marketing.checked),
      isScout: !!(scout && scout.checked),
      scoutGroup: group ? group.value : '',
      // Only the guest form asks for these; the app fills them in on the
      // profile Shopify creates at login. The logged-in form has no name
      // fields, so they stay empty and the app leaves that profile alone.
      firstName: fieldValue(root, 'first_name'),
      lastName: fieldValue(root, 'last_name')
    };
  }

  function takeStashed() {
    try {
      var raw = window.sessionStorage.getItem(INTENT_KEY);
      if (!raw) return null;
      window.sessionStorage.removeItem(INTENT_KEY);
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function join(intent) {
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(intent)
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw new Error(body && body.error ? body.error : 'HTTP ' + res.status);
        return body;
      });
    });
  }

  /* Logged-in form: enrol on submit. */
  function wireJoinForm(form) {
    if (form.__memberJoinBound) return;
    form.__memberJoinBound = true;

    var errorBox = form.querySelector('[data-member-join-error]');
    var successBox = form.querySelector('[data-member-join-success]');
    var submit = form.querySelector('[type="submit"]');

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var intent = readIntent(form);

      // Both consent boxes are required (client card, Magnus 2026-09-04):
      // the form is novalidate, so this is the only gate. A missing
      // marketing box is treated exactly like a missing terms box.
      var marketingRequired = !!form.querySelector('[data-member-marketing][required]');
      if (!intent.termsAccepted || (marketingRequired && !intent.marketingConsent)) {
        if (errorBox) {
          errorBox.hidden = false;
          errorBox.textContent = form.getAttribute('data-terms-error') ||
            'Du skal acceptere de påkrævede samtykker for at fortsætte.';
        }
        return;
      }
      if (errorBox) errorBox.hidden = true;
      if (submit) submit.disabled = true;

      join(intent).then(function () {
        if (successBox) successBox.hidden = false;
        // Membership gates every other surface on this page (prices, the
        // dashboard, the checkout block), and those are server-rendered —
        // so reload rather than trying to patch the page in place.
        window.location.reload();
      }).catch(function (err) {
        if (submit) submit.disabled = false;
        if (errorBox) {
          errorBox.hidden = false;
          errorBox.textContent = form.getAttribute('data-generic-error') ||
            'Vi kunne ikke oprette dit medlemskab lige nu. Prøv igen.';
        }
        if (window.console) console.error('[member-join]', err);
      });
    });
  }

  function rememberEmail(email) {
    try {
      if (email) window.localStorage.setItem(EMAIL_KEY, email);
    } catch (e) { /* storage disabled — the prefill is a nicety */ }
  }

  function rememberedEmail() {
    try {
      return window.localStorage.getItem(EMAIL_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function forgetEmail() {
    try { window.localStorage.removeItem(EMAIL_KEY); } catch (e) { /* noop */ }
  }

  function setBusy(form, busy) {
    var submit = form.querySelector('[type="submit"]');
    if (!submit) return;
    submit.disabled = busy;
    submit.setAttribute('aria-busy', busy ? 'true' : 'false');
    submit.classList.toggle('is-loading', busy);
  }

  /* Passwordless login with the address prefilled. Only
     /customer_authentication/login honours return_to (relative path), and
     `login_hint` on it survives Shopify's OAuth hop and fills the Mail field
     (verified 2026-08-28). encodeURIComponent: a "+" would arrive as a space. */
  function loginUrl(email, returnTo) {
    var locale = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'da';
    var url = '/customer_authentication/login?locale=' + encodeURIComponent(locale);
    if (returnTo) url += '&return_to=' + encodeURIComponent(returnTo);
    if (email) url += '&login_hint=' + encodeURIComponent(email);
    return url;
  }

  /* Session membership: the one cart attribute set every consumer reads
     (member-status.liquid, the member-pricing function, the checkout block).
     Same keys and values as the notify drawer (spejder-notify-klaviyo.js). */
  function writeSessionAttributes(intent, source) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        attributes: {
          membership_signup: 'yes',
          membership_terms_accepted: intent.termsAccepted ? 'yes' : 'no',
          membership_marketing_consent: intent.marketingConsent ? 'yes' : 'no',
          membership_signup_source: source,
          membership_is_scout: intent.isScout ? 'yes' : 'no',
          membership_scout_group: intent.isScout ? intent.scoutGroup || '' : ''
        }
      })
    }).then(function (res) {
      if (!res.ok) throw new Error('cart attributes rejected (' + res.status + ')');
      return res.json();
    });
  }

  /* Checkout prefill. Shopify's checkout takes its e-mail from the cart's
     buyerIdentity, which only the Storefront API can set — the Ajax cart
     token IS the Storefront cart id (gid://shopify/Cart/<token>). Needs a
     public Storefront API access token (theme setting → data-storefront-token
     on the drawer host); without one this is a silent no-op and only the
     login is prefilled. `/checkout?checkout[email]=` and the cart form's
     checkout[email] were both tested 2026-09-08 and do NOT prefill on this
     store; only cart permalinks do, and those rebuild the cart. */
  function setCartBuyerEmail(email, cart) {
    var host = document.querySelector('[data-storefront-token]');
    var token = host && host.getAttribute('data-storefront-token');
    if (!token || !email || !cart || !cart.token) return Promise.resolve();
    var version = host.getAttribute('data-storefront-api-version') || '2025-07';
    return fetch('/api/' + version + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({
        query: 'mutation SetBuyerEmail($cartId: ID!, $email: String!) {' +
          ' cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: { email: $email }) {' +
          ' userErrors { field message } } }',
        variables: { cartId: 'gid://shopify/Cart/' + cart.token, email: email }
      })
    }).then(function (res) { return res.json(); }).then(function (body) {
      var errors = body && body.data && body.data.cartBuyerIdentityUpdate && body.data.cartBuyerIdentityUpdate.userErrors;
      if ((errors && errors.length) || (body && body.errors)) {
        throw new Error(JSON.stringify(errors || body.errors));
      }
    }).catch(function (err) {
      // Prefill only. Never lets a failure reach the visitor.
      if (window.console) console.warn('[member-join] checkout prefill not set', err);
    });
  }

  /* Success state: the form's fields give way to [data-member-signup-done]
     ("Tak, du er nu tilmeldt!" + LOGIN). The login CTA gets the address
     prefilled; the rest of the drawer (banner, Fordele) stays. */
  function showSignedUp(form, email) {
    var done = form.querySelector('[data-member-signup-done]');
    // The drawer's "Skriv dig op" subheading sits outside the form and goes too.
    var scope = form.closest('[data-member-signup-scope]') || form;
    var fields = scope.querySelectorAll('[data-member-signup-fields]');
    fields.forEach(function (el) { el.hidden = true; });
    if (done) {
      var login = done.querySelector('[data-member-signup-login]');
      if (login) {
        login.setAttribute('href', loginUrl(email, login.getAttribute('data-return-to') || '/pages/kundeklub'));
      }
      done.hidden = false;
      var heading = done.querySelector('[data-member-signup-done-heading]');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      }
    }
    form.setAttribute('data-member-signed-up', 'true');
  }

  /* A visitor who already joined during this visit gets the success state
     server-rendered in place of the form (Magnus 2026-10-02). Its LOGIN is
     built the same way showSignedUp builds it, from the remembered address
     (wirePrefill skips these links). */
  function prefillSignedUpLogin() {
    var email = rememberedEmail();
    document.querySelectorAll('[data-member-signup-done]:not([hidden]) [data-member-signup-login]').forEach(function (login) {
      login.setAttribute('href', loginUrl(email, login.getAttribute('data-return-to') || '/pages/kundeklub'));
    });
  }

  /* Logged-out form: enrol on the spot.
   *
   * The submit event only fires once the browser's own validation (required
   * fields, e-mail format, both required consent boxes) has passed, so
   * nothing incomplete is ever posted. Order of operations:
   *   1. app proxy signup (the durable write — customer + membership)
   *   2. cart attributes (the session state every surface reads)
   *   3. remember the e-mail, prefill the cart's buyer identity, show "Tak"
   * A failed step 1 shows an error and re-enables the button — nothing is
   * half-done, the visitor simply tries again. A failed step 2 is logged and
   * still counts as signed up: the membership exists on the record, and the
   * orders/create webhook re-applies it at checkout anyway.
   */
  function wireGuestSignup(form) {
    if (form.__memberGuestBound) return;
    form.__memberGuestBound = true;

    var errorBox = form.querySelector('[data-member-join-error]');
    var source = form.getAttribute('data-member-signup-source') || 'signup_form';

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (form.getAttribute('data-member-signed-up') === 'true') return;

      var intent = readIntent(form);
      var email = fieldValue(form, 'email');
      if (errorBox) errorBox.hidden = true;
      setBusy(form, true);

      fetch(SIGNUP_ENDPOINT, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          email: email,
          firstName: intent.firstName,
          lastName: intent.lastName,
          termsAccepted: intent.termsAccepted,
          marketingConsent: intent.marketingConsent,
          isScout: intent.isScout,
          scoutGroup: intent.scoutGroup,
          source: source
        })
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (body) {
          if (!res.ok) throw new Error(body && body.error ? body.error : 'HTTP ' + res.status);
          return body;
        });
      }).then(function () {
        rememberEmail(email);
        return writeSessionAttributes(intent, source).then(function (cart) {
          return setCartBuyerEmail(email, cart);
        }).catch(function (err) {
          if (window.console) console.error('[member-join] session attributes failed', err);
        });
      }).then(function () {
        setBusy(form, false);
        showSignedUp(form, email);
        wirePrefill();
      }).catch(function (err) {
        setBusy(form, false);
        if (errorBox) {
          errorBox.hidden = false;
          errorBox.textContent = form.getAttribute('data-generic-error') ||
            'Vi kunne ikke oprette dit medlemskab lige nu. Prøv igen.';
        }
        if (window.console) console.error('[member-join] guest signup failed', err);
      });
    });

    /* Member prices are server-rendered. The drawer stays put after the
       signup (that IS the success state), and the page re-renders at member
       prices the moment the drawer is closed. The on-page section has no
       drawer to close: its surroundings update on the next navigation. */
    var dialog = form.closest('dialog');
    if (dialog) {
      dialog.addEventListener('theme:popup:onclose', function () {
        if (form.getAttribute('data-member-signed-up') !== 'true') return;
        // Started from the cart drawer / cart page (spejder.js records the
        // origin on the host)? Come back with the cart open — the visitor
        // was looking at it and it now shows member prices.
        var host = form.closest('[data-member-signup-drawer-host]');
        var fromCart = host && host.getAttribute('data-member-signup-origin') === 'cart';
        var url = new URL(window.location.href);
        if (fromCart) url.searchParams.set(JOINED_PARAM, 'cart');
        window.location.href = url.pathname + url.search + url.hash;
      });
    }
  }

  /* Prefill the login with the remembered address. Every header/menu login
     link points at routes.account_login_url, which on this store renders as
     the external shopify.com/…/account URL and drops anything appended, so
     the links are rewritten to /customer_authentication/login with
     login_hint (and a return_to back to this page). A signed-in customer has
     no login links and nothing to prefill: the address is dropped then. */
  function wirePrefill() {
    var loggedIn = !!document.querySelector('[data-member-join-form]') ||
      !!document.querySelector('[data-customer-logged-in="true"]');
    if (loggedIn) { forgetEmail(); return; }
    var email = rememberedEmail();
    if (!email) return;
    var returnTo = window.location.pathname + window.location.search;
    document.querySelectorAll('a[href]').forEach(function (a) {
      if (a.hasAttribute('data-member-signup-login')) return;
      var href = a.getAttribute('href') || '';
      var isLogin = /\/customer_authentication\/login/.test(href) ||
        /^https?:\/\/(www\.)?shopify\.com\/\d+\/account/.test(href) ||
        /^\/account\/login/.test(href);
      if (!isLogin || /login_hint=/.test(href)) return;
      a.setAttribute('href', loginUrl(email, returnTo));
    });
  }

  /* Returning identified — replay a stashed intent (legacy path).
   *
   * Guest forms no longer stash (they enrol directly, 2026-09-08); this only
   * still fires for a visitor who submitted the old "TILMELD OG LOGIN" form
   * before the change and comes back logged in with the stash intact.
   *
   * Two triggers, either is enough:
   *   · ?join=1 on the URL — the return_to the signup forms hand to
   *     /customer_authentication/login (new customer accounts honour
   *     `return_to`, NOT `return_url`, relative paths only).
   *   · the visitor is logged in and a stash exists — covers every way the
   *     sign-in flow can land them elsewhere on the storefront (account
   *     portal → "back to shop", a locale hop, a bookmarked page). Before
   *     this, a broken return meant the contact existed and was e-mail-
   *     subscribed but never became a member (QA card #4, 2026-08-25).
   *     Login IS the e-mail verification, so replaying here is exactly as
   *     safe as the ?join=1 path.
   *
   * "Logged in" is read off the DOM: [data-member-join-form] only renders for
   * a signed-in customer (the kundeklub signup section, and the signup drawer
   * that is on every page), so nothing extra is threaded through Liquid.
   */
  function autoJoinOnReturn() {
    var params = new URLSearchParams(window.location.search);
    var viaParam = params.get('join') === '1';
    var loggedIn = !!document.querySelector('[data-member-join-form]');
    if (!viaParam && !loggedIn) return;

    var intent = takeStashed();
    // No stash, or consent not actually given: show the form, never assume.
    if (!intent || intent.termsAccepted !== true) return;

    join(intent).then(function () {
      // Drop ?join=1 so a refresh doesn't look like a second enrolment.
      params.delete('join');
      var qs = params.toString();
      window.location.replace(
        window.location.pathname + (qs ? '?' + qs : '')
      );
    }).catch(function (err) {
      if (window.console) console.error('[member-join] auto-join failed', err);
    });
  }

  /* After the post-signup reload from a cart surface: drop the marker (a
     refresh must not re-open the cart again) and re-open the cart drawer.
     theme:cart:refresh re-fetches the drawer and, via cart-items' build()
     → theme:product:added, opens it. */
  function reopenCartAfterJoin() {
    var url = new URL(window.location.href);
    if (url.searchParams.get(JOINED_PARAM) !== 'cart') return;
    url.searchParams.delete(JOINED_PARAM);
    window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
  }

  function init() {
    document.querySelectorAll('[data-member-join-form]').forEach(wireJoinForm);
    document.querySelectorAll('[data-member-signup-form]').forEach(wireGuestSignup);
    autoJoinOnReturn();
    prefillSignedUpLogin();
    wirePrefill();
    reopenCartAfterJoin();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, {once: true});
  } else {
    init();
  }
})();
